import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  inject,
  input,
  Input,
  InputSignal,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges
} from "@angular/core";
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { FormlyFieldConfig, FormlyFormOptions, FormlyModule } from "@ngx-formly/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { combineLatest } from 'rxjs';
import { FormViewModel } from '../../models/form.model';
import { PermissionService } from '../../services/auth/permission.service';
import { isDefined, isDefinedNotEmpty, isUndefinedOrEmpty } from '../../util';

@Component({
  imports: [
    CommonModule,
    FormlyModule,
    ReactiveFormsModule,
    TranslateModule
  ],
  selector: "app-form",
  standalone: true,
  styleUrls: ["form.component.scss"],
  templateUrl: "form.component.html"
})
export class FormComponent implements OnInit, OnChanges {
  // Inputs
  @Input() additionalButtons: any[];
  @Input() callback: Function;
  @Input() closeRoute: string;
  @Input() disableSubmitButton: boolean;
  public fields: InputSignal<FormlyFieldConfig[]> = input.required<FormlyFieldConfig[]>();
  @Input() loadingDone: boolean;
  @Input() model: {};
  // RBAC: when set, the Submit button is hidden unless the caller holds this permission. Optional
  // and backward compatible (unset = always shown). UX only — the API re-authorizes every write —
  // and fail-OPEN until the permission set loads (mirrors ActionBarComponent/DatatableComponent),
  // so a slow permission load never blocks a legitimately-permitted user's Submit.
  @Input() requiredPermission?: string;

  closeRouteThis: any;
  disableSubmitButtonThis = false;
  form = new FormGroup({});
  loadingIndicator = false;
  modelSelf: any = {};
  options: FormlyFormOptions = {};
  translated = false;
  translations: any = {};
  public vm: FormViewModel;

  private destroyRef: DestroyRef;

  public constructor(
    private router: Router,
    private translateService: TranslateService,
    private permissionService: PermissionService
  ) {
    this.destroyRef = inject(DestroyRef);
  }

  /**
   * Whether the Submit button may be shown, given the optional requiredPermission. No permission
   * required -> always true; fail-OPEN while the permission set has not loaded (isReady === false)
   * so a legitimately-permitted user is never blocked by a slow load; then the real per-permission
   * check. UX only — the API is the authorization boundary and re-validates every write.
   */
  public get canSubmit(): boolean {
    if (!this.requiredPermission) {
      return true;
    }

    if (!this.permissionService.isReady) {
      return true;
    }

    return this.permissionService.has(this.requiredPermission);
  }

  public ngOnInit(): void {
    this.vm = {
      fields: signal(this.translateBulk(this.fields()))
    };

    this.modelSelf = this.model;
    this.closeRouteThis = this.closeRoute;
    this.disableSubmitButtonThis = this.disableSubmitButton;

    combineLatest([
      this.translateService.onLangChange,
      this.translateService.onTranslationChange
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => {
        this.vm.fields.set(this.translateBulk(this.fields()));
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (isDefined(changes.loadingDone?.currentValue) && !changes.loadingDone.firstChange &&
        changes.loadingDone.currentValue !== changes.loadingDone.previousValue &&
        changes.loadingDone.currentValue === true) {
      this.loadingIndicator = false;
    }

    if (isDefined(changes.fields?.currentValue) && !changes.fields.firstChange) {
      this.vm.fields.set(this.translateBulk(this.fields()));
    }
  }

  loaderVisibility(value: boolean) {
    this.loadingIndicator = value;
  }

  resetForm() {
    this.form.reset();
  }

  updateModel(data: any) {
    this.modelSelf = data;
  }

  public updateFields(fields: FormlyFieldConfig[]) {
    setTimeout((): void => {
      this.vm.fields.set(this.translateBulk(fields));
    }, 50); // Wait until component is created
  }

  close() {
    this.router.navigate([this.closeRouteThis]);
  }

  submit() {
    if (this.form.valid) {
      this.loadingIndicator = true;
      this.callback();
    }
  }

  private translateBulk(fields: FormlyFieldConfig[]): FormlyFieldConfig[] {
    return fields.map((field: FormlyFieldConfig): FormlyFieldConfig => {
      return {
        ...field,
        props: {
          ...field.props,
          placeholder:
            isDefinedNotEmpty(field.props?.placeholder)
              ? this.translateService.instant(field.props.placeholder)
              : '',
          label: isDefinedNotEmpty(field.props?.label)
            ? this.translateService.instant(field.props.label)
            : '',
          options: isDefined(field.props?.options)
            ? (field.props.options as any[]).map((option: any): any => {
              if (isUndefinedOrEmpty(option.name) && isUndefinedOrEmpty(option.value)) {
                return;
              }
              return {
                ...option,
                name: isDefinedNotEmpty(option.name) ? this.translateService.instant(option.name) : null,
                value: isDefinedNotEmpty(option.value) ? this.translateService.instant(option.value) : null,
              };
            })
            : null
        }
      }
    });
  }
}
